<template>
    <a-spin tip="Carregando..." :spinning="spinning">
        <div class="container">
            <a-card>
                <template #title>
                    <a-breadcrumb separator=">">
                        <a-breadcrumb-item>
                            <FileOutlined />
                            <a><span>Camadas</span></a>
                        </a-breadcrumb-item>
                        <a-breadcrumb-item>Listar Camadas</a-breadcrumb-item>
                    </a-breadcrumb>
                </template>
                <template #extra>
                    <a-button :icon="h(PlusOutlined)" @click="newRegister">Novo Registro</a-button>
                </template>

                <div>
                    <!-- Toolbar de filtro -->
                    <div class="table-toolbar">
                        <h3>Camadas</h3>
                        <a-input-search
                            v-model:value="search"
                            placeholder="Filtrar por nome"
                            allowClear
                            :enter-button="false"
                            style="max-width: 320px"
                        />
                    </div>

                    <a-table
                        :columns="columns"
                        :data-source="filteredData"
                        class="components-table-demo-nested"
                        :rowKey="record => record.id"
                        :rowExpandable="record => record.layers && record.layers.length > 0"
                        :pagination="false"
                        :customRow="record => customRow(record, 'normal')"
                    >
                        <template #bodyCell="{ column, record }">
                            <!-- Alça de arraste (pais) -->
                            <template v-if="column.dataIndex === 'order'">
                                <a-tooltip title="Arraste para reordenar">
                                    <a-button
                                        shape="circle"
                                        class="drag-handle-btn"
                                        :draggable="true"
                                        @dragstart.stop="onTopDragStart(record, $event, 'normal')"
                                        @dragend.stop="onTopDragEnd"
                                    >
                                        <template #icon><MenuOutlined /></template>
                                    </a-button>
                                </a-tooltip>
                            </template>

                            <!-- Dados de texto -->
                            <template v-else-if="['name','objective'].includes(column.dataIndex)">
                                <span>{{ record[column.dataIndex] }}</span>
                            </template>
                            <template v-else-if="column.dataIndex === 'author_user_name'">
                                <span>{{ formatAuthor(record) }}</span>
                            </template>
                            <template v-else-if="column.dataIndex === 'created_at'">
                                <span>{{ formatDateTime(record.created_at) }}</span>
                            </template>

                            <!-- Ativo: ícones -->
                            <template v-else-if="column.dataIndex === 'active'">
                                <CheckOutlined v-if="record.active" class="icon-active-true" aria-label="Ativo" />
                                <CloseOutlined v-else class="icon-active-false" aria-label="Inativo" />
                            </template>

                            <!-- Ações (pais) -->
                            <template v-else-if="column.dataIndex === 'actions'">
                                <a-space>
                                    <a-tooltip title="Ver">
                                        <a-button type="primary" shape="circle" class="center-flex view" @click="onView(record)">
                                            <template #icon><EyeOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Alterar">
                                        <a-button type="primary" shape="circle" class="center-flex edit" @click="onEdit(record)">
                                            <template #icon><FormOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Excluir">
                                        <a-button type="primary" danger shape="circle" class="center-flex delete" @click="confirmDelete(record)">
                                            <template #icon><DeleteOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Vincular Tutorial">
                                        <a-button type="primary" shape="circle" class="center-flex faq" @click="onTutorial(record)">
                                            <template #icon><BookOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Permissões">
                                        <a-button type="primary" shape="circle" class="center-flex permission" @click="onPermissions(record)">
                                            <template #icon><SafetyOutlined /></template>
                                        </a-button>
                                    </a-tooltip>
                                    <a-tooltip :title="isPublicMap(record) ? 'Copiar link público' : 'Mapa não é público'">
                                        <a-button
                                            type="primary"
                                            shape="circle"
                                            class="center-flex copy-link"
                                            :disabled="!isPublicMap(record)"
                                            @click="copyPublicLink(record)"
                                        >
                                            <template #icon><CopyOutlined /></template>
                                        </a-button>
                                    </a-tooltip>
                                </a-space>
                            </template>
                        </template>

                        <!-- Filhos -->
                        <template #expandedRowRender="{ record }">
                            <div class="child-table-container" v-if="record.layers && record.layers.length">
                                <table class="child-table">
                                    <thead>
                                    <tr>
                                        <th style="width:110px">Ordem</th>
                                        <th>Nome</th>
                                        <th>Tipo</th>
                                        <th>Autor</th>
                                        <th>Criado em</th>
                                        <th style="width:80px">Ativo</th>
                                        <th style="width:260px">Ações</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr
                                        v-for="layer in record.layers"
                                        :key="layer.id"
                                        v-bind="childRowProps(record, layer)"
                                    >
                                        <td>
                                            <div class="child-order-cell">
                                                <a-tooltip title="Arraste para reordenar">
                                                    <a-button
                                                        shape="circle"
                                                        class="drag-child-btn"
                                                        :draggable="true"
                                                        @dragstart.stop="onChildDragStart(record, layer, $event)"
                                                        @dragend.stop="onChildDragEnd"
                                                    >
                                                        <template #icon><MenuOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>
                                            </div>
                                        </td>

                                        <td><span>{{ layer.name }}</span></td>
                                        <td><span>{{ layer.layer_type }}</span></td>
                                        <td><span>{{ formatAuthor(layer) }}</span></td>
                                        <td><span>{{ formatDateTime(layer.created_at) }}</span></td>

                                        <td style="text-align:center">
                                            <CheckOutlined v-if="layer.active" class="icon-active-true" aria-label="Ativo" />
                                            <CloseOutlined v-else class="icon-active-false" aria-label="Inativo" />
                                        </td>

                                        <td>
                                            <a-space>
                                                <a-tooltip title="Ver">
                                                    <a-button type="primary" shape="circle" class="center-flex view" @click="onView(layer)">
                                                        <template #icon><EyeOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Alterar">
                                                    <a-button type="primary" shape="circle" class="center-flex edit" @click="onEdit(layer)">
                                                        <template #icon><FormOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Excluir">
                                                    <a-button type="primary" danger shape="circle" class="center-flex delete" @click="confirmDelete(layer)">
                                                        <template #icon><DeleteOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Vincular Tutorial">
                                                    <a-button type="primary" shape="circle" class="center-flex faq" @click="onTutorial(layer)">
                                                        <template #icon><BookOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Permissões">
                                                    <a-button type="primary" shape="circle" class="center-flex permission" @click="onPermissions(layer)">
                                                        <template #icon><SafetyOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>
                                                <a-tooltip :title="isPublicMap(layer) ? 'Copiar link público' : 'Mapa não é público'">
                                                    <a-button
                                                        type="primary"
                                                        shape="circle"
                                                        class="center-flex copy-link"
                                                        :disabled="!isPublicMap(layer)"
                                                        @click="copyPublicLink(layer)"
                                                    >
                                                        <template #icon><CopyOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>
                                            </a-space>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div v-else class="child-table-container">
                                <a-typography-text type="secondary">Sem itens filhos.</a-typography-text>
                            </div>
                        </template>
                    </a-table>
                </div>

                <div>
                    <!-- Toolbar de filtro -->
                    <div class="table-toolbar">
                        <h3>Camadas Raster</h3>
                        <a-input-search
                            v-model:value="searchRaster"
                            placeholder="Filtrar por nome"
                            allowClear
                            :enter-button="false"
                            style="max-width: 320px"
                        />
                    </div>

                    <a-table
                        :columns="columns"
                        :data-source="rasterFilteredData"
                        class="components-table-demo-nested"
                        :rowKey="record => record.id"
                        :rowExpandable="record => record.layers && record.layers.length > 0"
                        :pagination="false"
                        :customRow="record => customRow(record, 'raster')"
                    >
                        <template #bodyCell="{ column, record }">
                            <!-- Alça de arraste (pais) -->
                            <template v-if="column.dataIndex === 'order'">
                                <a-tooltip title="Arraste para reordenar">
                                    <a-button
                                        shape="circle"
                                        class="drag-handle-btn"
                                        :draggable="true"
                                        @dragstart.stop="onTopDragStart(record, $event, 'raster')"
                                        @dragend.stop="onTopDragEnd"
                                    >
                                        <template #icon><MenuOutlined /></template>
                                    </a-button>
                                </a-tooltip>
                            </template>

                            <!-- Dados de texto -->
                            <template v-else-if="['name','objective'].includes(column.dataIndex)">
                                <span>{{ record[column.dataIndex] }}</span>
                            </template>
                            <template v-else-if="column.dataIndex === 'author_user_name'">
                                <span>{{ formatAuthor(record) }}</span>
                            </template>
                            <template v-else-if="column.dataIndex === 'created_at'">
                                <span>{{ formatDateTime(record.created_at) }}</span>
                            </template>

                            <!-- Ativo: ícones -->
                            <template v-else-if="column.dataIndex === 'active'">
                                <CheckOutlined v-if="record.active" class="icon-active-true" aria-label="Ativo" />
                                <CloseOutlined v-else class="icon-active-false" aria-label="Inativo" />
                            </template>

                            <!-- Ações (pais) -->
                            <template v-else-if="column.dataIndex === 'actions'">
                                <a-space>
                                    <a-tooltip title="Ver">
                                        <a-button type="primary" shape="circle" class="center-flex view" @click="onView(record)">
                                            <template #icon><EyeOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Alterar">
                                        <a-button type="primary" shape="circle" class="center-flex edit" @click="onEdit(record)">
                                            <template #icon><FormOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Excluir">
                                        <a-button type="primary" danger shape="circle" class="center-flex delete" @click="confirmDelete(record)">
                                            <template #icon><DeleteOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Vincular Tutorial">
                                        <a-button type="primary" shape="circle" class="center-flex faq" @click="onTutorial(record)">
                                            <template #icon><BookOutlined /></template>
                                        </a-button>
                                    </a-tooltip>

                                    <a-tooltip title="Permissões">
                                        <a-button type="primary" shape="circle" class="center-flex permission" @click="onPermissions(record)">
                                            <template #icon><SafetyOutlined /></template>
                                        </a-button>
                                    </a-tooltip>
                                    <a-tooltip :title="isPublicMap(record) ? 'Copiar link público' : 'Mapa não é público'">
                                        <a-button
                                            type="primary"
                                            shape="circle"
                                            class="center-flex copy-link"
                                            :disabled="!isPublicMap(record)"
                                            @click="copyPublicLink(record)"
                                        >
                                            <template #icon><CopyOutlined /></template>
                                        </a-button>
                                    </a-tooltip>
                                </a-space>
                            </template>
                        </template>

                        <!-- Filhos -->
                        <template #expandedRowRender="{ record }">
                            <div class="child-table-container" v-if="record.layers && record.layers.length">
                                <table class="child-table">
                                    <thead>
                                    <tr>
                                        <th style="width:110px">Ordem</th>
                                        <th>Nome</th>
                                        <th>Tipo</th>
                                        <th>Autor</th>
                                        <th>Criado em</th>
                                        <th style="width:80px">Ativo</th>
                                        <th style="width:260px">Ações</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr
                                        v-for="layer in record.layers"
                                        :key="layer.id"
                                        v-bind="childRowProps(record, layer)"
                                    >
                                        <td>
                                            <div class="child-order-cell">
                                                <a-tooltip title="Arraste para reordenar">
                                                    <a-button
                                                        shape="circle"
                                                        class="drag-child-btn"
                                                        :draggable="true"
                                                        @dragstart.stop="onChildDragStart(record, layer, $event)"
                                                        @dragend.stop="onChildDragEnd"
                                                    >
                                                        <template #icon><MenuOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>
                                            </div>
                                        </td>

                                        <td><span>{{ layer.name }}</span></td>
                                        <td><span>{{ layer.layer_type }}</span></td>
                                        <td><span>{{ formatAuthor(layer) }}</span></td>
                                        <td><span>{{ formatDateTime(layer.created_at) }}</span></td>

                                        <td style="text-align:center">
                                            <CheckOutlined v-if="layer.active" class="icon-active-true" aria-label="Ativo" />
                                            <CloseOutlined v-else class="icon-active-false" aria-label="Inativo" />
                                        </td>

                                        <td>
                                            <a-space>
                                                <a-tooltip title="Ver">
                                                    <a-button type="primary" shape="circle" class="center-flex view" @click="onView(layer)">
                                                        <template #icon><EyeOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Alterar">
                                                    <a-button type="primary" shape="circle" class="center-flex edit" @click="onEdit(layer)">
                                                        <template #icon><FormOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Excluir">
                                                    <a-button type="primary" danger shape="circle" class="center-flex delete" @click="confirmDelete(layer)">
                                                        <template #icon><DeleteOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Vincular Tutorial">
                                                    <a-button type="primary" shape="circle" class="center-flex faq" @click="onTutorial(layer)">
                                                        <template #icon><BookOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>

                                                <a-tooltip title="Permissões">
                                                    <a-button type="primary" shape="circle" class="center-flex permission" @click="onPermissions(layer)">
                                                        <template #icon><SafetyOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>
                                                <a-tooltip :title="isPublicMap(layer) ? 'Copiar link público' : 'Mapa não é público'">
                                                    <a-button
                                                        type="primary"
                                                        shape="circle"
                                                        class="center-flex copy-link"
                                                        :disabled="!isPublicMap(layer)"
                                                        @click="copyPublicLink(layer)"
                                                    >
                                                        <template #icon><CopyOutlined /></template>
                                                    </a-button>
                                                </a-tooltip>
                                            </a-space>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div v-else class="child-table-container">
                                <a-typography-text type="secondary">Sem itens filhos.</a-typography-text>
                            </div>
                        </template>
                    </a-table>
                </div>

                <!-- Modal Vincular Tutorial -->
                <a-modal
                    v-model:open="tutorialModalOpen"
                    :title="tutorialModalTitle"
                    :footer="null"
                    @cancel="closeTutorial"
                    destroyOnClose
                >
                    <FaqPicker
                        :selected-id="modalRow?.faq_id || null"
                        @save="handleFaqSave"
                        @cancel="closeTutorial"
                    />
                </a-modal>

                <!-- Modal Permissões -->
                <a-modal
                    v-model:open="permissionsModalOpen"
                    :title="permissionsModalTitle"
                    :footer="null"
                    @cancel="closePermissions"
                    destroyOnClose
                >
                    <UserGroupPicker
                        :selected-ids="modalRow?.users_groups_ids || []"
                        @save="handlePermissionsSave"
                        @cancel="closePermissions"
                    />
                </a-modal>
            </a-card>
        </div>
    </a-spin>
</template>

<script setup>
import {ref, onMounted, computed, h} from 'vue'
import {
    FileOutlined,
    MenuOutlined,
    EyeOutlined,
    FormOutlined,
    CopyOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseOutlined,
    BookOutlined,
    SafetyOutlined, PlusOutlined
} from '@ant-design/icons-vue'

import { Modal, notification } from 'ant-design-vue'
import FaqPicker from '../../Components/FaqPicker.vue'
import UserGroupPicker from '../../Components/UserGroupPicker.vue'
import { openNotification } from '../../../../utils/utilities.js'

const layersDataSource  = ref([])
const rasterLayersDataSource = ref([])
const spinning    = ref(false)
const route       = '/api/maps/layers'

const columns = [
    { title: 'Ordem',    dataIndex: 'order',    key: 'order',    width: 110, sorter: (a,b)=>a.order-b.order },
    { title: 'Nome',     dataIndex: 'name',     key: 'name',     width: 150, sorter: (a,b)=>String(a.name).localeCompare(String(b.name)) },
    { title: 'Tipo',     dataIndex: 'layer_type', key: 'layer_type', width: 150 },
    { title: 'Autor',    dataIndex: 'author_user_name', key: 'author_user_name', width: 180, customRender: ({ record }) => formatAuthor(record) },
    { title: 'Criado em', dataIndex: 'created_at', key: 'created_at', width: 180, sorter: (a,b) => new Date(a.created_at || 0) - new Date(b.created_at || 0), customRender: ({ record }) => formatDateTime(record.created_at) },
    { title: 'Ativo',    dataIndex: 'active',   key: 'active',   width: 100 },
    { title: 'Ações',    dataIndex: 'actions',  key: 'actions',  width: 260 },
]

function formatAuthor (record) {
    // Laravel serializa relações em snake_case no JSON (`author_user`, não `authorUser`).
    return record?.author_user?.name || record?.authorUser?.name || 'N/A'
}

function formatDateTime (value) {
    if (!value) return 'N/A'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return 'N/A'
    return d.toLocaleString('pt-BR')
}

const newRegister = () => {
    window.location.href = '/maps/layers/create'
}
const search = ref('')
const searchRaster = ref('')
const filteredData = computed(() => {
    const term = (search.value || '').trim().toLowerCase()
    if (!term) return layersDataSource.value

    return layersDataSource.value
        .map(parent => {
            const parentMatches = String(parent.name || '').toLowerCase().includes(term)
            const children = Array.isArray(parent.layers) ? parent.layers.filter(
                c => String(c.name || '').toLowerCase().includes(term)
            ) : []

            if (parentMatches) return parent // mantém todos os filhos
            if (children.length) return { ...parent, layers: children } // mostra só filhos que casam
            return null
        })
        .filter(Boolean)
})

const rasterFilteredData = computed(() => {
    const term = (searchRaster.value || '').trim().toLowerCase()
    if (!term) return rasterLayersDataSource.value

    return rasterLayersDataSource.value
        .map(parent => {
            const parentMatches = String(parent.name || '').toLowerCase().includes(term)
            const children = Array.isArray(parent.layers) ? parent.layers.filter(
                c => String(c.name || '').toLowerCase().includes(term)
            ) : []

            if (parentMatches) return parent // mantém todos os filhos
            if (children.length) return { ...parent, layers: children } // mostra só filhos que casam
            return null
        })
        .filter(Boolean)
})

onMounted(load)

async function handleFaqSave(faqId) {
    const id = modalRow.value?.id
    try {
        const response = await window.axios.put(`${route}/${id}`, { faq_id: faqId })
        openNotification('success', response.data.message || 'Camada atualizada!')
        await load()
    } catch (e) {
        openNotification('error', e.message || 'Erro ao atualizar!')
        await load()
    }
    closeTutorial()
}

async function handlePermissionsSave(groupIds) {
    const id = modalRow.value?.id
    try {
        const response = await window.axios.put(`${route}/${id}`, { users_groups: groupIds })
        openNotification('success', response.data.message || 'Camada atualizada!')
        await load()
    } catch (e) {
        openNotification('error', e.message || 'Erro ao atualizar!')
        await load()
    }
    closeTutorial()
    closePermissions()
}

async function load () {
    try {
        spinning.value = true
        const params = new URLSearchParams(window.location.search)
        const module = params.get('module')
        const requestParams = new URLSearchParams()

        requestParams.set('onlyWithoutGroup', '')

        if (module) {
            requestParams.set('module', module)
        }

        const { data } = await window.axios.get(`${route}?${requestParams.toString()}`)

        const normalLayers = data.data.filter(layer => !layer.is_raster)

        const rasterLayers = data.data.filter(layer => layer.is_raster)

        layersDataSource.value = normalLayers || []
        rasterLayersDataSource.value = rasterLayers || []
        sortDeep(layersDataSource.value)
        sortDeep(rasterLayersDataSource.value)
    } finally {
        spinning.value = false
    }
}

function sortDeep (list) {
    list.sort((a,b)=>a.order-b.order)
    list.forEach(i => i.layers?.length && sortDeep(i.layers))
}

/* Helpers de reorder */
function moveInArray (arr, from, to) {
    if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return
    const item = arr.splice(from, 1)[0]
    arr.splice(to, 0, item)
}
function renumberOrders (arr) {
    arr.forEach((r, i) => { r.order = i + 1 })
}

/* Pais — drag em tempo real */
let draggingTop = { id: null, index: -1, type: 'normal' }

function getTopLevelList(type = 'normal') {
    return type === 'raster' ? rasterLayersDataSource.value : layersDataSource.value
}

function onTopDragStart (record, e, type = 'normal') {
    const list = getTopLevelList(type)
    draggingTop.id = record.id
    draggingTop.type = type
    draggingTop.index = list.findIndex(r => r.id === record.id)
    try { e.dataTransfer.setData('text/plain', String(record.id)) } catch {}
    e.dataTransfer.effectAllowed = 'move'
}
function onTopDragEnd () {
    draggingTop = { id: null, index: -1, type: 'normal' }
}
function customRow (record, type = 'normal') {
    return {
        onDragover: (e) => {
            if (!draggingTop.id || draggingTop.type !== type) return
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            const list = getTopLevelList(type)
            const overIndex = list.findIndex(r => r.id === record.id)
            if (overIndex !== -1 && overIndex !== draggingTop.index) {
                moveInArray(list, draggingTop.index, overIndex)
                draggingTop.index = overIndex
                renumberOrders(list)
            }
        },
        onDrop: async (e) => {
            if (!draggingTop.id || draggingTop.type !== type) return
            e.preventDefault()
            await saveReorder(null, getTopLevelList(type))
            draggingTop = { id: null, index: -1, type: 'normal' }
        }
    }
}

/* Filhos — drag em tempo real por pai */
let draggingChild = { parentId: null, id: null, index: -1 }

function onChildDragStart (parent, item, e) {
    const idx = parent.layers.findIndex(x => x.id === item.id)
    draggingChild = { parentId: parent.id, id: item.id, index: idx }
    try { e.dataTransfer.setData('text/plain', String(item.id)) } catch {}
    e.dataTransfer.effectAllowed = 'move'
}
function onChildDragEnd () {
    draggingChild = { parentId: null, id: null, index: -1 }
}
function childRowProps (parent, item) {
    return {
        onDragover: (e) => {
            if (!draggingChild.id || draggingChild.parentId !== parent.id) return
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            const overIndex = parent.layers.findIndex(x => x.id === item.id)
            if (overIndex !== -1 && overIndex !== draggingChild.index) {
                moveInArray(parent.layers, draggingChild.index, overIndex)
                draggingChild.index = overIndex
                renumberOrders(parent.layers)
            }
        },
        onDrop: async (e) => {
            if (!draggingChild.id || draggingChild.parentId !== parent.id) return
            e.preventDefault()
            await saveReorder(parent.id, parent.layers)
            draggingChild = { parentId: null, id: null, index: -1 }
        }
    }
}

async function saveReorder(parentId, listRef) {
    const payload = {
        parent_id: parentId ?? null,
        items: listRef.map((r, i) => ({ id: r.id, order: i + 1 })),
    }
    try {
        const response = await window.axios.put(`${route}/reorder`, payload)
        openNotification('success', response.data.message || 'Ordem salva!')
        await load()
    } catch (e) {
        openNotification('error', e.message || 'Erro ao salvar ordem')
        await load()
    }
}

/* ===== Modais ===== */
const tutorialModalOpen = ref(false)
const permissionsModalOpen = ref(false)
const modalRow = ref(null)

const tutorialModalTitle = computed(() =>
    modalRow.value ? `Vincular Tutorial — ${modalRow.value.name}` : 'Vincular Tutorial'
)
const permissionsModalTitle = computed(() =>
    modalRow.value ? `Permissões — ${modalRow.value.name}` : 'Permissões'
)

function onTutorial(row) { modalRow.value = row; tutorialModalOpen.value = true }
function onPermissions(row) { modalRow.value = row; permissionsModalOpen.value = true }
function closeTutorial() { tutorialModalOpen.value = false; modalRow.value = null }
function closePermissions() { permissionsModalOpen.value = false; modalRow.value = null }

/* Eventos */
function onView (row)  { window.open(`/maps/layers/${row.id}/view`, '_blank') }
function onEdit (row)  {  window.location.href =`/maps/layers/${row.id}/edit` }

const confirmDelete = (row) => {
    Modal.confirm({
        title: 'Excluir camada?',
        content: `Tem certeza que deseja excluir "${row.name}"? Esta ação não pode ser desfeita.`,
        okText: 'Excluir',
        okType: 'danger',
        cancelText: 'Cancelar',
        centered: true,
        async onOk() { await onDelete(row) },
    })
}
const onDelete = async (row) => {
    try {
        const response = await window.axios.delete(`${route}/${row.id}`)
        openNotification('success', response.data.message || 'Excluído com sucesso!')
        await load()
    } catch (e) {
        openNotification('error', e.message || 'Falha ao Excluir!')
        await load()
    }
}

function getPublicLink(row) {
    return `${window.location.origin}/maps/public/layers/${row.id}/view`
}

function isPublicMap(row) {
    return Boolean(row?.is_public)
}

async function copyPublicLink(row) {
    const link = getPublicLink(row)
    try {
        if (!navigator?.clipboard?.writeText) throw new Error('Clipboard API indisponível')
        await navigator.clipboard.writeText(link)
        openNotification('success', 'Link público copiado para a área de transferência.')
    } catch (_) {
        openNotification('error', 'Não foi possível copiar o link público.')
    }
}
</script>

<style scoped>
.table-toolbar { display:flex; justify-content:space-between; margin: 30px 0 12px 0; }

.child-table-container { display:flex; justify-content:center; width:100% }
.child-table { border-collapse:collapse; width:60%; max-width:800px; border:1px solid #ddd }
.child-table th, .child-table td { border:1px solid #ddd; padding:8px; text-align:center }
.child-order-cell { display:flex; align-items:center; justify-content:center }

/* alças de arraste */
.drag-handle-btn,
.drag-child-btn { cursor: grab; }
.drag-handle-btn:active,
.drag-child-btn:active { cursor: grabbing; }

/* cores dos ícones ativo/inativo */
.icon-active-true  { color: #52c41a; font-size: 16px; }
.icon-active-false { color: #ff4d4f; font-size: 16px; }

/* cores botões */
.center-flex { display:inline-flex; justify-content:center; align-items:center; }
.view { background-color: green; }
.edit { background-color: blue; }
.delete { background-color: red; }
.faq { background-color: black; }
.permission { color: black; background-color: yellow; }
.copy-link { background-color: #6f42c1; }
</style>
